import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Users, Search, X, Download, Upload } from 'lucide-react';
import { contactService } from '../services';
import { messageHandler } from '../services/message-handler.service';
import type { Contact, ContactFilterOptions } from '../types/contact';
import PageHeader from '../components/ui/PageHeader';
import Button from '../components/ui/Button';
import EmptyState from '../components/ui/EmptyState';
import ContactList from '../components/contacts/ContactList';
import ContactForm from '../components/contacts/ContactForm';
import ContactFilters from '../components/contacts/ContactFilters';
import ImportContactsModal from '../components/contacts/ImportContactsModal';
import ExportSuccessModal from '../components/contacts/ExportSuccessModal';
import Modal from '../components/ui/Modal';
import Pagination from '../components/ui/Pagination';
import LoadingSpinner from '../components/ui/LoadingSpinner';
import Alert from '../components/ui/Alert';
import { AlertCircle } from 'lucide-react';

type ModalType = 'create' | 'edit' | 'import' | '';

interface ModalState {
  type: ModalType;
  title?: string;
}

const DEFAULT_LIMIT = 20;

function Contacts() {
  const [modal, setModal] = useState<ModalState>({ type: '' });
  const [editingContact, setEditingContact] = useState<Contact | null>(null);
  const [filters, setFilters] = useState<ContactFilterOptions>({
    page: 1,
    limit: DEFAULT_LIMIT,
  });
  const [selectedContacts, setSelectedContacts] = useState<number[]>([]);
  const [exportResult, setExportResult] = useState<{
    downloadUrl: string;
    count: number;
    isOpen: boolean;
  }>({
    downloadUrl: '',
    count: 0,
    isOpen: false,
  });
  const [formError, setFormError] = useState<string | null>(null);
  const queryClient = useQueryClient();

  // Queries
  const { data: contactsResponse, isLoading } = useQuery({
    queryKey: ['contacts', filters],
    queryFn: () => contactService.getContacts(filters),
    keepPreviousData: true,
  });

  // Mutations
  const createMutation = useMutation({
    mutationFn: (data: Partial<Contact>) => contactService.createContact(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contacts'] });
      messageHandler.success('Contato criado com sucesso!');
      setFormError(null);
      closeModal();
    },
    onError: (error: any) => {
      // Handle specific error messages from the backend
      if (error.response?.data?.error) {
        setFormError(error.response.data.error);
      } else {
        messageHandler.error(error.message || 'Erro ao criar contato');
      }
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: Contact) => contactService.updateContact(data.id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contacts'] });
      messageHandler.success('Contato atualizado com sucesso!');
      setFormError(null);
      closeModal();
    },
    onError: (error: any) => {
      // Handle specific error messages from the backend
      if (error.response?.data?.error) {
        setFormError(error.response.data.error);
      } else {
        messageHandler.error(error.message || 'Erro ao atualizar contato');
      }
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => contactService.deleteContact(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contacts'] });
      messageHandler.success('Contato excluído com sucesso!');
    },
    onError: (error: any) => {
      messageHandler.error(error.message || 'Erro ao excluir contato');
    },
  });

  const importMutation = useMutation({
    mutationFn: (file: File) => contactService.importContacts(file),
    onSuccess: data => {
      queryClient.invalidateQueries({ queryKey: ['contacts'] });
      messageHandler.success(
        `${data.imported} contatos importados com sucesso! ${data.skipped} contatos ignorados.`
      );
      if (data.errors.length > 0) {
        messageHandler.warning(`Ocorreram ${data.errors.length} erros durante a importação.`);
      }
      closeModal();
    },
    onError: (error: any) => {
      messageHandler.error(error.message || 'Erro ao importar contatos');
    },
  });

  // Handlers
  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormError(null);

    const formData = new FormData(e.currentTarget);
    const data = {
      contact: formData.get('contact') as string,
      name: formData.get('name') as string,
      is_custom_name: formData.get('is_custom_name') === 'on',
      channel: formData.get('channel') as string,
      key: formData.get('key') as string || undefined,
      is_group: formData.get('is_group') === 'on',
      ia_respond_always: formData.get('ia_respond_always') === 'on',
      personal_assistant_id: formData.get('personal_assistant_id')
        ? parseInt(formData.get('personal_assistant_id') as string)
        : undefined,
      whatsapp_id: formData.get('whatsapp_id')
        ? parseInt(formData.get('whatsapp_id') as string)
        : undefined,
      disable_chatbot: formData.get('disable_chatbot') === 'on',
      type: formData.get('type') as string,
      tags: formData.get('tags')
        ? (formData.get('tags') as string).split(',').map(t => t.trim()).filter(Boolean)
        : null,
      disable_survey: formData.get('disable_survey') === 'on',
    };

    if (editingContact) {
      updateMutation.mutate({ ...editingContact, ...data });
    } else {
      createMutation.mutate(data);
    }
  };

  const handleDelete = (contact: Contact) => {
    messageHandler.confirm('Excluir Contato', 'Tem certeza que deseja excluir este contato?', () =>
      deleteMutation.mutate(contact.id)
    );
  };

  const handleImport = async (file: File) => {
    await importMutation.mutateAsync(file);
  };

  const handleExport = async () => {
    try {
      const channel = filters.channel;
      const selectedIds = selectedContacts.length > 0 ? selectedContacts : undefined;

      const result = await contactService.exportContacts(channel, selectedIds);

      // Show the export success modal with download link
      setExportResult({
        downloadUrl: result.download_url,
        count: result.count,
        isOpen: true,
      });
    } catch (error: any) {
      messageHandler.error(error.message || 'Erro ao exportar contatos');
    }
  };

  const handleFilterChange = (newFilters: ContactFilterOptions) => {
    setFilters(newFilters);
  };

  const handleResetFilters = () => {
    setFilters({
      page: 1,
      limit: DEFAULT_LIMIT,
    });
  };

  const handlePageChange = (page: number) => {
    setFilters(prev => ({
      ...prev,
      page,
    }));
    // Scroll to top when page changes
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSelectContact = (contact: Contact, isSelected: boolean) => {
    if (isSelected) {
      setSelectedContacts(prev => [...prev, contact.id]);
    } else {
      setSelectedContacts(prev => prev.filter(id => id !== contact.id));
    }
  };

  const closeModal = () => {
    setModal({ type: '' });
    setEditingContact(null);
    setFormError(null);
  };

  const closeExportModal = () => {
    setExportResult(prev => ({ ...prev, isOpen: false }));
  };

  const contacts = contactsResponse?.data || [];
  const pagination = contactsResponse?.pagination;
  const totalRecords = pagination?.total || 0;
  const isFiltered = Object.keys(filters).some(
    key => key !== 'page' && key !== 'limit' && filters[key as keyof ContactFilterOptions]
  );

  return (
    <div className="py-6">
      <div className="px-4 sm:px-6 lg:px-8">
        <PageHeader
          title="Contatos"
          description="Gerencie seus contatos para comunicação."
          actions={
            <div className="flex space-x-2">
              <Button variant="secondary" onClick={() => setModal({ type: 'import' })}>
                <Upload className="h-4 w-4 mr-2" />
                Importar
              </Button>
              <Button variant="secondary" onClick={handleExport} disabled={totalRecords === 0}>
                <Download className="h-4 w-4 mr-2" />
                Exportar {selectedContacts.length > 0 ? `(${selectedContacts.length})` : ''}
              </Button>
              <Button
                onClick={() => {
                  setEditingContact(null);
                  setModal({ type: 'create' });
                }}
              >
                <Plus className="h-4 w-4 mr-2" />
                Novo Contato
              </Button>
            </div>
          }
        />

        <ContactFilters
          filters={filters}
          onFilterChange={handleFilterChange}
          onResetFilters={handleResetFilters}
        />

        {/* Total Records Count */}
        <div className="mb-4 flex items-center text-sm text-gray-600 dark:text-gray-400">
          <Users className="h-4 w-4 mr-2" />
          <span>
            Total de registros: <strong>{totalRecords}</strong>
            {selectedContacts.length > 0 && (
              <span className="ml-2">
                | Selecionados: <strong>{selectedContacts.length}</strong>
              </span>
            )}
          </span>
        </div>

        {isLoading ? (
          <div className="flex justify-center py-8">
            <LoadingSpinner size="lg" />
          </div>
        ) : (
          <>
            <ContactList
              contacts={contacts}
              onEdit={contact => {
                setEditingContact(contact);
                setModal({ type: 'edit' });
              }}
              onDelete={handleDelete}
              onSelectContact={handleSelectContact}
              selectedContacts={selectedContacts}
              isLoading={isLoading}
            />

            {pagination && pagination.last_page > 1 && (
              <div className="mt-6">
                <Pagination
                  currentPage={pagination.current_page}
                  totalPages={pagination.last_page}
                  onPageChange={handlePageChange}
                />
              </div>
            )}
          </>
        )}

        {/* Create/Edit Modal */}
        <Modal
          isOpen={modal.type === 'create' || modal.type === 'edit'}
          onClose={closeModal}
          title={editingContact ? 'Editar Contato' : 'Novo Contato'}
        >
          {formError && (
            <div className="mb-4">
              <Alert variant="error" icon={AlertCircle}>
                {formError}
              </Alert>
            </div>
          )}
          <ContactForm
            contact={editingContact}
            onSubmit={handleSubmit}
            isLoading={createMutation.isPending || updateMutation.isPending}
            onClose={closeModal}
          />
        </Modal>

        {/* Import Modal */}
        <ImportContactsModal
          isOpen={modal.type === 'import'}
          onClose={closeModal}
          onImport={handleImport}
          isLoading={importMutation.isPending}
        />

        {/* Export Success Modal */}
        <ExportSuccessModal
          isOpen={exportResult.isOpen}
          onClose={closeExportModal}
          downloadUrl={exportResult.downloadUrl}
          count={exportResult.count}
        />
      </div>
    </div>
  );
}

export default Contacts;
